import React, { useState, useEffect } from 'react';
import { Upload, X, Link as LinkIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { getImageUrl } from '../../lib/utils';

const ImageUpload = ({ value, onChange, label, category = null }) => {
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [imageUrl, setImageUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(category || '');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    setImageUrl(value || '');
  }, [value]);

  useEffect(() => {
    // Fetch available categories
    fetch(`${BACKEND_URL}/api/upload/categories`)
      .then(res => res.json())
      .then(data => setCategories(data.categories || []))
      .catch(err => console.error('Error fetching categories:', err));
  }, [BACKEND_URL]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('يرجى اختيار صورة فقط');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الصورة يجب أن يكون أقل من 5MB');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedCategory) {
        formData.append('category', selectedCategory);
      }

      const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      // Use the full URL if available, otherwise use file_path with BACKEND_URL
      const uploadedUrl = data.url || `${BACKEND_URL}${data.file_path}`;
      setImageUrl(uploadedUrl);
      onChange(uploadedUrl);
    } catch (error) {
      console.error('Upload error:', error);
      alert('فشل رفع الصورة');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (url) => {
    setImageUrl(url);
    onChange(url);
  };

  const handleRemove = () => {
    setImageUrl('');
    onChange('');
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Upload Mode Selector */}
      <div className="flex gap-2 mb-3">
        <Button
          type="button"
          size="sm"
          variant={uploadMode === 'url' ? 'default' : 'outline'}
          onClick={() => setUploadMode('url')}
          className="flex-1"
        >
          <LinkIcon className="w-4 h-4 ml-2" />
          رابط خارجي
        </Button>
        <Button
          type="button"
          size="sm"
          variant={uploadMode === 'file' ? 'default' : 'outline'}
          onClick={() => setUploadMode('file')}
          className="flex-1"
        >
          <Upload className="w-4 h-4 ml-2" />
          رفع ملف
        </Button>
      </div>

      {/* Category Selector (only for file upload) */}
      {uploadMode === 'file' && !category && categories.length > 0 && (
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] focus:border-transparent"
        >
          <option value="">اختر المجلد...</option>
          {categories.map((cat) => (
            <option key={cat.key} value={cat.key}>
              📁 {cat.name}
            </option>
          ))}
        </select>
      )}

      {/* URL Input Mode */}
      {uploadMode === 'url' && (
        <input
          type="url"
          value={imageUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="https://example.com/image.jpg"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] focus:border-transparent"
        />
      )}

      {/* File Upload Mode */}
      {uploadMode === 'file' && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0F388E] transition-colors">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
            disabled={uploading}
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="w-12 h-12 text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              {uploading ? 'جاري الرفع...' : 'اضغط لرفع صورة'}
            </span>
            <span className="text-xs text-gray-400 mt-1">الحد الأقصى: 5MB</span>
            {selectedCategory && (
              <span className="text-xs text-[#0F388E] mt-2 font-medium">
                📁 {categories.find(c => c.key === selectedCategory)?.name}
              </span>
            )}
          </label>
        </div>
      )}

      {/* Image Preview */}
      {imageUrl && (
        <div className="relative inline-block">
          <img
            src={getImageUrl(imageUrl)}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
            }}
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute top-2 left-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
